<template>
  <div
    class="offcanvas offcanvas-fullscreen-sm-down offcanvas-end"
    style="width: 620px;"
    tabindex="-1"
    id="offcanvasManageProducts"
    ref="offcanvas"
    data-bs-backdrop="static"
  >
    <div class="bg-top-nav border offcanvas-header py-2">
      {{ $t('Manage Branch Menu') }}
      <button
        type="button"
        style="font-size: small;"
        class="btn-close shadow-none text-reset"
        data-bs-dismiss="offcanvas"
        aria-label="Close"
      />
    </div>

    <div class="offcanvas-body" v-if="loading">
      <placeholder-content :is-load="true" />
    </div>
    <div class="offcanvas-body" v-else-if="!hasPermission">
      <access-denied />
    </div>

    <div class="offcanvas-body p-0" v-else>
      <div class="bg-white border-bottom px-3 py-2 sticky-top">
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <b style="font-size: 18px;">{{ data.name }}</b>
            <span v-if="data.code" class="text-muted small ms-2">{{ data.code }}</span>
          </div>
          <span class="badge bg-theme-subtle text-theme align-self-center">
            {{ activeIds.length }} / {{ totalProducts }} {{ $t('on sale') }}
          </span>
        </div>
        <p class="text-muted small mb-2 mt-1">
          <i class="fa-solid fa-circle-info me-1" />
          {{ $t('Tick a product to sell it at this branch. Variant availability is managed on the product page.') }}
        </p>
        <div class="input-group input-group-sm">
          <span class="input-group-text bg-white"><i class="fa-solid fa-search" /></span>
          <input
            type="search"
            class="form-control shadow-none"
            :placeholder="$t('search') + '...'"
            v-model.trim="search"
          >
        </div>
      </div>

      <div class="px-3 py-3">
        <template v-for="category in filteredCategories" :key="category.id">
          <div class="mb-3">
            <div class="d-flex justify-content-between align-items-center bg-light border rounded px-2 py-1 mb-2">
              <span class="fw-bold small">{{ $ln(category) }}</span>
              <div class="form-check form-switch mb-0">
                <input
                  class="form-check-input shadow-none"
                  type="checkbox"
                  :id="'cat-all-' + category.id"
                  :checked="isCategoryAllSelected(category)"
                  @change="toggleCategory(category, $event)"
                >
                <label class="form-check-label small text-secondary cursor-pointer" :for="'cat-all-' + category.id">
                  {{ $t('All') }}
                </label>
              </div>
            </div>
            <div class="row g-2 px-1">
              <div
                v-for="product in category.products"
                :key="product.branch_product_id"
                class="col-12 col-md-6"
              >
                <div class="form-check">
                  <input
                    class="form-check-input shadow-none"
                    style="box-shadow: none !important;"
                    type="checkbox"
                    :id="'bp-' + product.branch_product_id"
                    :value="product.branch_product_id"
                    v-model="activeIds"
                  >
                  <label class="form-check-label small cursor-pointer" :for="'bp-' + product.branch_product_id">
                    {{ $ln(product) }}
                    <span v-if="product.is_config" class="badge bg-info-subtle text-info border border-info-subtle ms-1">
                      {{ $t('Configurale') }}
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </template>

        <div v-if="!filteredCategories.length" class="text-muted text-center py-4">
          {{ $t('No Available Data') }}
        </div>
      </div>
    </div>

    <div class="bg-white border-top justify-content-center modal-footer py-2" v-if="!loading && hasPermission">
      <ladda
        text="Save"
        :btn-class="'btn btn-theme btn-block me-2'"
        :loading="loadingSave"
        @click="handleSave"
      />
      <button
        type="button"
        data-bs-dismiss="offcanvas"
        class="border-dark-subtle btn-sm btn btn-light"
      >
        {{ $t('Cancel') }}
      </button>
    </div>
  </div>
</template>

<script>
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import AccessDenied from '@/plugins/AccessDenied.vue';
import Ladda from '@/plugins/Ladda.vue';
import { Branches } from '@/services/endpoint/branches.service';

export default {
  components: {
    PlaceholderContent,
    AccessDenied,
    Ladda,
  },
  props: {
    id: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      loadingSave: false,
      hasPermission: false,
      data: {},
      categories: [],
      activeIds: [],
      search: '',
    };
  },
  computed: {
    totalProducts() {
      return this.categories.reduce((sum, c) => sum + c.products.length, 0);
    },
    filteredCategories() {
      const kw = this.search.trim().toLowerCase();
      if (!kw) return this.categories;
      return this.categories
        .map(c => ({
          ...c,
          products: c.products.filter(p => (this.$ln(p) || p.name).toLowerCase().includes(kw)),
        }))
        .filter(c => c.products.length);
    },
  },
  methods: {
    async fetchData() {
      this.loading = true;
      await Branches.getUpdateProduct(this.id).then(res => {
        const payload = res.data.data;
        this.data = { name: payload.name, code: payload.code, active: payload.active };
        this.categories = payload.categories || [];
        this.activeIds = this.categories
          .flatMap(c => c.products)
          .filter(p => p.is_active)
          .map(p => p.branch_product_id);
        this.hasPermission = true;
        this.loading = false;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
        }
        this.loading = false;
        throw err;
      });
    },
    isCategoryAllSelected(category) {
      return category.products.length > 0
        && category.products.every(p => this.activeIds.includes(p.branch_product_id));
    },
    toggleCategory(category, event) {
      const ids = category.products.map(p => p.branch_product_id);
      if (event.target.checked) {
        const set = new Set(this.activeIds);
        ids.forEach(id => set.add(id));
        this.activeIds = [...set];
      } else {
        this.activeIds = this.activeIds.filter(id => !ids.includes(id));
      }
    },
    async handleSave() {
      this.loadingSave = true;
      await Branches.updateProduct(this.id, { branch_product_ids: this.activeIds }).then(() => {
        this.$toast.success('<B>' + this.$t('Success') + '</B>\n' + this.$t('Update Success!'));
        $(this.$refs.offcanvas).offcanvas('hide');
        this.loadingSave = false;
      }).catch(err => {
        this.$toast.warn(this.$t(err?.response?.data?.message) || this.$t('Something went wrong'));
        this.loadingSave = false;
      });
    },
  },
  mounted() {
    $(this.$refs.offcanvas).offcanvas('show');

    $(this.$refs.offcanvas).on('shown.bs.offcanvas', () => {
      this.fetchData();
    });

    $(this.$refs.offcanvas).on('hidden.bs.offcanvas', () => {
      this.$emit('close');
    });
  },
};
</script>
